import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { FormatType } from "@enums/FormatType";
import customTableSort from "@methods/customTableSort";
import { GenericListManagerBase } from "../GenericListManagerBase";
import { GenericPaginationListConfig } from "@app/shared/models/GenericPaginationList/GenericPaginationListConfig";
import { GenericPaginationColumnInfo } from "../GenericPaginationColumnInfo";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "generic-pagination-list-manager",
    templateUrl: "./generic-pagination-list-manager.component.html",
    styleUrls: ["./generic-pagination-list-manager.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope({ scope: "shared/generic-managers/generic-pagination-list-manager", alias: "genericPaginationListManager" })],
})
export class GenericPaginationListManagerComponent
    extends GenericListManagerBase
    implements OnInit
{
    @Input() config: GenericPaginationListConfig;
    @Output() onOpenTab = new EventEmitter<void>();

    defaultPageSize = 10;
    pageSize = this.defaultPageSize;

    columnInfo: GenericPaginationColumnInfo[] = [];

    // Pagestuff
    allData: any[];
    sortedData: any[];
    currentData: any[];
    sortColumn?: any;
    sortAsc = true;

    currentPage = 0;
    pageCount = 0;
    paginationAmount = 10;
    isShowingAll = false;
    actionTitle: string;

    isLoaded = false;

    FormatType = FormatType;

    async ngOnInit() {
        this.baseConfig = this.config;
        this.config.reload = async () => {
            this.tableData = null;
            this.baseConfig = this.config;
            await this.loadPage().then(() => {
                this.setAdditionalColumns();
                this.allData = this.data;
                if(this.isShowingAll){
                    this.currentData = this.allData;
                }
                this.currentPage = 0;
                this.pageCount = Math.ceil(this.allData.length / this.paginationAmount);
                this.sortData();
            });
        };

        await this.loadPage().then(() => {
            if (this.config.overviewSortBy) {
                this.sortColumn = this.config.overviewSortBy;
                this.sortAsc = this.config.overviewSortAsc ?? true;
            }
            this.setSourceData();
        });

        this.sortColumn = this.config.sortProp;
        if (this.config.defaultPageSize) {
            this.defaultPageSize = this.config.defaultPageSize;
        }
        if (this.config.allowEdit) {
            this.setActionTitle();
        }
    }

    setActionTitle() {
        if (this.config.name.endsWith("ies")) {
            this.actionTitle = this.config.name.slice(0, -3) + "y";
        } else if (this.config.name.endsWith("s")) {
            this.actionTitle = this.config.name.slice(0, -1);
        } else {
            this.actionTitle = this.config.name;
        }
    }

    setColumns() {
        this.config.fields.forEach((field) => {
            if (!field.hideInOverview) {
                this.columnInfo.push({
                    fieldName: field.fieldName,
                    prettyName: field.prettyName,
                    formatType: field.formatType,
                    headerCssClass: field.headerCssClass,
                });
            }
        });
    }

    setSourceData() {
        this.setAdditionalColumns();

        this.allData = this.data;

        this.currentPage = 0;
        this.pageCount = Math.ceil(this.allData.length / this.paginationAmount);
        this.setColumns();
        this.sortData();

        this.isLoaded = true;
    }

    setAdditionalColumns() {
        this.config.setAdditionalColumns(this.data);
    }

    setSort(header: string) {
        if (this.config.isSortableByColumn === false) {
            return;
        }
        if (header !== this.sortColumn) {
            this.sortColumn = header;
            if (this.sortColumn.toLowerCase() != this.config.sortProp.toLowerCase()) {
                this.sortAsc = true;
            } else {
                this.sortAsc = false;
            }
        } else {
            this.sortAsc = !this.sortAsc;
        }

        this.sortData();
    }

    setPagination(page: number) {
        if (page >= 0 && page <= this.pageCount - 1) {
            this.currentPage = page;
            this.paginateData();
        }
    }

    sortData() {
        if (!this.sortColumn) {
            this.sortColumn = this.columnInfo[0].fieldName;
        }

        const formatType: FormatType = this.columnInfo.find(
            (ci) => ci.fieldName === this.sortColumn,
        ).formatType;

        const isNumeric =
            formatType &&
            (formatType === FormatType.DecimalFormat || formatType === FormatType.CurrencyFormat);
        const isDate =
            formatType &&
            (formatType === FormatType.DateFormat ||
                formatType === FormatType.DateShortTimeFormat ||
                formatType === FormatType.DateTimeFormat);

        const isString = !isNumeric && !isDate;

        const compareMethod = customTableSort(
            this.sortAsc,
            this.sortColumn,
            isString,
            isNumeric,
            isDate,
        );

        this.sortedData = this.allData.sort(compareMethod);

        if (!this.isShowingAll) {
            this.paginateData();
        }
    }

    paginateData() {
        const startIndex = this.currentPage * this.paginationAmount;
        const endIndex = +startIndex + +this.paginationAmount;
        this.currentData = this.sortedData.slice(startIndex, endIndex);
    }

    openItem(row: any) {
        if (this.config.editOverride) {
            this.config.editOverride(row);
        }
    }

    toggleAllItems() {
        if (this.isShowingAll) {
            this.paginateData();
            this.isShowingAll = false;
            this.pageSize = this.defaultPageSize;
            this.setPagination(0);
        } else {
            this.setPagination(0);
            this.isShowingAll = true;
            this.pageSize = this.allData.length;

            this.currentData = this.allData;
        }
    }
}
